import { Check, X, Clock, User, Building2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ApprovalTimelineProps {
  managerApprovalStatus: string;
  managerApprovedAt: string | null;
  managerApproverEmail?: string | null;
  managerApprovalReason?: string | null;
  hrApprovalStatus: string;
  hrApprovedAt: string | null;
  hrApproverEmail?: string | null;
  hrApprovalReason?: string | null;
}

const ApprovalTimeline = ({
  managerApprovalStatus,
  managerApprovedAt,
  managerApproverEmail,
  managerApprovalReason,
  hrApprovalStatus,
  hrApprovedAt,
  hrApproverEmail,
  hrApprovalReason
}: ApprovalTimelineProps) => {
  const getStepStatus = (status: string) => {
    switch (status) {
      case 'approved':
        return { icon: Check, color: 'text-green-600 bg-green-100', label: 'Approuvé' };
      case 'rejected':
        return { icon: X, color: 'text-red-600 bg-red-100', label: 'Refusé' };
      case 'pending':
        return { icon: Clock, color: 'text-yellow-600 bg-yellow-100', label: 'En attente' };
      case 'not_started':
      default:
        return { icon: Clock, color: 'text-gray-400 bg-gray-100', label: 'Non commencé' };
    }
  };

  const managerStep = getStepStatus(managerApprovalStatus);
  const hrStep = getStepStatus(hrApprovalStatus);

  const ManagerIcon = managerStep.icon;
  const HrIcon = hrStep.icon;

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-muted-foreground">Statut des approbations</h4>
      
      <div className="relative">
        {/* Connecting line */}
        <div className="absolute left-5 top-10 bottom-10 w-0.5 bg-border" />
        
        {/* Manager Step */}
        <div className="relative flex gap-4 pb-6">
          <div className={`rounded-full p-2 z-10 ${managerStep.color}`}>
            <User className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">Manager</span>
              <Badge 
                variant={managerApprovalStatus === 'approved' ? 'default' : 
                        managerApprovalStatus === 'rejected' ? 'destructive' : 'secondary'}
                className={managerApprovalStatus === 'approved' ? 'bg-green-100 text-green-800' : ''}
              >
                <ManagerIcon className="h-3 w-3 mr-1" />
                {managerStep.label}
              </Badge>
            </div>
            
            {managerApprovedAt && (
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(managerApprovedAt).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                {managerApproverEmail && (
                  <span className="ml-2">par {managerApproverEmail}</span>
                )}
              </p>
            )}
            
            {managerApprovalReason && managerApprovalStatus === 'rejected' && (
              <p className="text-sm text-red-600 mt-1 bg-red-50 p-2 rounded">
                Motif: {managerApprovalReason}
              </p>
            )}
          </div>
        </div>

        {/* HR Step */}
        <div className="relative flex gap-4">
          <div className={`rounded-full p-2 z-10 ${hrStep.color}`}>
            <Building2 className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">Ressources Humaines</span>
              <Badge 
                variant={hrApprovalStatus === 'approved' ? 'default' : 
                        hrApprovalStatus === 'rejected' ? 'destructive' : 'secondary'}
                className={hrApprovalStatus === 'approved' ? 'bg-green-100 text-green-800' : ''}
              >
                <HrIcon className="h-3 w-3 mr-1" />
                {hrStep.label}
              </Badge>
            </div>
            
            {hrApprovedAt && (
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(hrApprovedAt).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                {hrApproverEmail && (
                  <span className="ml-2">par {hrApproverEmail}</span>
                )}
              </p>
            )}
            
            {hrApprovalReason && hrApprovalStatus === 'rejected' && (
              <p className="text-sm text-red-600 mt-1 bg-red-50 p-2 rounded">
                Motif: {hrApprovalReason}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalTimeline;